import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../../Components/Common/HelpArticleLink";
import { HELP_ARTICLE_SETTINGS_PAYMENTS } from "../../../helpers/helpCenter";
import {
  buildCheckoutFlagsFromResponse,
  buildDepositFlagsFromResponse,
  CHECKOUT_GATEWAY_METHOD,
  CHECKOUT_TERMINAL_GATEWAY_METHOD,
  CHECKOUT_PAYMENT_METHOD_KEYS,
  DEPOSIT_COLLECTION_METHOD_KEYS,
  DEPOSIT_GATEWAY_METHOD,
  getPaymentMethodSettings,
  PaymentMethodFlags,
  PaymentMethodsSettingsData,
  updatePaymentMethodSettings,
} from "../../../services/paymentMethodSettingsService";

type MethodSectionProps = {
  title: string;
  help: string;
  methodKeys: readonly string[];
  flags: PaymentMethodFlags;
  effective: string[];
  gatewayMethods: readonly string[];
  labelPrefix: string;
  onToggle: (method: string, enabled: boolean) => void;
};

const MethodSection = ({
  title,
  help,
  methodKeys,
  flags,
  effective,
  gatewayMethods,
  labelPrefix,
  onToggle,
}: MethodSectionProps) => {
  const { t } = useTranslation();

  return (
    <Card className="mb-4">
      <CardHeader>
        <h5 className="card-title mb-0">{title}</h5>
      </CardHeader>
      <CardBody>
        <p className="text-muted small mb-4">{help}</p>
        <div className="d-flex flex-column gap-3">
          {methodKeys.map((method) => {
            const isEnabled = flags[method] ?? false;
            const isEffective = effective.includes(method);
            const isGateway = gatewayMethods.includes(method);
            const gatewayAvailable = effective.includes(method);
            const showAutoOffWarning = isGateway && isEnabled && !isEffective;
            const disableGatewayToggle = isGateway && !gatewayAvailable && !isEnabled;

            return (
              <div
                key={method}
                className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 border rounded p-3"
              >
                <div className="form-check form-switch mb-0">
                  <Input
                    id={`payment-method-${method}`}
                    type="checkbox"
                    role="switch"
                    className="form-check-input"
                    checked={isEnabled}
                    disabled={disableGatewayToggle}
                    onChange={(event) => onToggle(method, event.target.checked)}
                  />
                  <Label className="form-check-label" htmlFor={`payment-method-${method}`}>
                    {t(`${labelPrefix}.${method}`)}
                  </Label>
                </div>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  {showAutoOffWarning ? (
                    <Badge color="warning" className="text-wrap text-start">
                      {t("settings.payments.gateway.autoOffWarning")}
                    </Badge>
                  ) : null}
                  {disableGatewayToggle ? (
                    <small className="text-muted">
                      {t("settings.payments.gateway.connectHint")}{" "}
                      <Link to="/settings/integrations" className="text-decoration-underline">
                        {t("settings.payments.gateway.connectLink")}
                      </Link>
                    </small>
                  ) : null}
                  {isEnabled && isEffective ? (
                    <Badge color="success">{t("settings.payments.status.active")}</Badge>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
};

const PaymentSettingsPage = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [effective, setEffective] = useState<PaymentMethodsSettingsData["effective"]>({
    checkout: [],
    deposit: [],
  });
  const [checkoutFlags, setCheckoutFlags] = useState<PaymentMethodFlags>({});
  const [depositFlags, setDepositFlags] = useState<PaymentMethodFlags>({});
  const [initialCheckoutFlags, setInitialCheckoutFlags] = useState<PaymentMethodFlags>({});
  const [initialDepositFlags, setInitialDepositFlags] = useState<PaymentMethodFlags>({});

  useEffect(() => {
    document.title = t("settings.payments.pageTitle");
  }, [t]);

  const applyResponse = useCallback((data: PaymentMethodsSettingsData) => {
    const nextCheckout = buildCheckoutFlagsFromResponse(data.raw.checkout, data.effective.checkout);
    const nextDeposit = buildDepositFlagsFromResponse(data.raw.deposit, data.effective.deposit);

    setEffective(data.effective);
    setCheckoutFlags(nextCheckout);
    setDepositFlags(nextDeposit);
    setInitialCheckoutFlags(nextCheckout);
    setInitialDepositFlags(nextDeposit);
  }, []);

  const loadSettings = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getPaymentMethodSettings();
      applyResponse(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("settings.payments.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [applyResponse, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const isDirty = useMemo(
    () =>
      JSON.stringify(checkoutFlags) !== JSON.stringify(initialCheckoutFlags)
      || JSON.stringify(depositFlags) !== JSON.stringify(initialDepositFlags),
    [checkoutFlags, depositFlags, initialCheckoutFlags, initialDepositFlags]
  );

  const handleCheckoutToggle = (method: string, enabled: boolean) => {
    setCheckoutFlags((current) => ({
      ...current,
      [method]: enabled,
    }));
  };

  const handleDepositToggle = (method: string, enabled: boolean) => {
    setDepositFlags((current) => ({
      ...current,
      [method]: enabled,
    }));
  };

  const extractValidationMessage = (error: any): string | undefined => {
    const details = error?.details as Record<string, string[]> | undefined;

    if (!details) {
      return undefined;
    }

    const firstField = Object.keys(details)[0];
    return firstField ? details[firstField]?.[0] : undefined;
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const response = await updatePaymentMethodSettings({
        enabled_checkout_payment_methods: checkoutFlags,
        enabled_deposit_collection_methods: depositFlags,
      });

      applyResponse(response.data);
      toast.success(response.message || t("settings.payments.toast.saveSuccess"));
    } catch (error: any) {
      toast.error(
        extractValidationMessage(error) || error?.data || t("settings.payments.toast.saveError")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      <ToastContainer closeButton pauseOnHover />
      <Container fluid>
        <BreadCrumb
          title={t("settings.payments.breadcrumb.title")}
          pageTitle={t("settings.payments.breadcrumb.pageTitle")}
        />
        <div className="d-flex justify-content-end mb-3">
          <HelpArticleLink articlePath={HELP_ARTICLE_SETTINGS_PAYMENTS} />
        </div>
        <Row className="justify-content-center">
          <Col lg={9} xl={8}>
            <Alert color="info" className="mb-4">
              {t("settings.payments.pageHelper")}
            </Alert>

            {loading ? (
              <div className="d-flex justify-content-center py-5">
                <Spinner color="primary" />
              </div>
            ) : (
              <>
                <MethodSection
                  title={t("settings.payments.checkout.title")}
                  help={t("settings.payments.checkout.help")}
                  methodKeys={CHECKOUT_PAYMENT_METHOD_KEYS}
                  flags={checkoutFlags}
                  effective={effective.checkout}
                  gatewayMethods={[
                    CHECKOUT_GATEWAY_METHOD,
                    CHECKOUT_TERMINAL_GATEWAY_METHOD,
                  ]}
                  labelPrefix="studio.orders.checkout.payment.method"
                  onToggle={handleCheckoutToggle}
                />

                <MethodSection
                  title={t("settings.payments.deposit.title")}
                  help={t("settings.payments.deposit.help")}
                  methodKeys={DEPOSIT_COLLECTION_METHOD_KEYS}
                  flags={depositFlags}
                  effective={effective.deposit}
                  gatewayMethods={[DEPOSIT_GATEWAY_METHOD]}
                  labelPrefix="studio.agenda.deposit.channel"
                  onToggle={handleDepositToggle}
                />

                <div className="d-flex justify-content-end">
                  <Button color="primary" disabled={!isDirty || saving} onClick={handleSave}>
                    {saving ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        {t("settings.payments.actions.saving")}
                      </>
                    ) : (
                      t("settings.payments.actions.save")
                    )}
                  </Button>
                </div>
              </>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PaymentSettingsPage;
